// Typed client for the lab endpoints.
//
// The only place in the frontend that calls `api.*` for this domain. Every
// method maps the wire DTO through a pure parse adapter, so callers always get
// the domain form (timestamps as `Date`). Caching is the hooks' job; validation
// is the server's.

import { api, ApiError, clinicApi } from '@/lib/api';
import type {
  ConfirmLabDocumentInput,
  CreateLabLaboratoryInput,
  CreateLabOrderInput,
  LabCatalogueDTO,
  LabLaboratory,
  LabDocument,
  LabDocumentDTO,
  LabOrderDetail,
  LabOrderDetailDTO,
  LabOrderSummary,
  LabOrderSummaryDTO,
  LabRejectionReason,
  LabReportRow,
  LabReportRowDTO,
  LabReportsQuery,
  LabSpecimen,
  LabSpecimenDTO,
  LabTest,
  LabTestDTO,
  LabWorklistQuery,
  LabWorklistRow,
  UpdateLabLaboratoryInput,
  UpdateLabOrderInput,
} from '@/types/lab';
import type { SpecimenType } from '@synapse/lab-tests';

// ---------- Parse adapters (wire → domain) ----------

/**
 * Convert an ISO timestamp into a Date, failing loudly rather than letting an
 * `Invalid Date` leak into the UI, where it surfaces as the string "Invalid
 * Date" or NaN arithmetic far from the cause. Same rule as `patientsClient`.
 */
function toDate(iso: string): Date {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    throw new ApiError('PARSE_ERROR', `Invalid timestamp from server: ${iso}`, 0);
  }
  return d;
}

function toDateOrNull(iso: string | null): Date | null {
  return iso === null ? null : toDate(iso);
}

export function parseLabTest(dto: LabTestDTO): LabTest {
  return { ...dto, resultedAt: toDateOrNull(dto.resultedAt) };
}

export function parseLabSpecimen(dto: LabSpecimenDTO): LabSpecimen {
  return {
    ...dto,
    collectedAt: toDate(dto.collectedAt),
    rejectedAt: toDateOrNull(dto.rejectedAt),
  };
}

export function parseLabDocument(dto: LabDocumentDTO): LabDocument {
  return {
    ...dto,
    uploadedAt: toDate(dto.uploadedAt),
    deletedAt: toDateOrNull(dto.deletedAt),
  };
}

export function parseLabOrderSummary(dto: LabOrderSummaryDTO): LabOrderSummary {
  return {
    // `laboratoryId` / `laboratoryName` need no mapping — they are plain strings
    // and the spread already carries them. Adding explicit lines for them would
    // be duplication that only rots.
    ...dto,
    createdAt: toDate(dto.createdAt),
    cancelledAt: toDateOrNull(dto.cancelledAt),
    tests: dto.tests.map(parseLabTest),
    specimens: dto.specimens.map(parseLabSpecimen),
  };
}

export function parseLabOrderDetail(dto: LabOrderDetailDTO): LabOrderDetail {
  return {
    ...parseLabOrderSummary(dto),
    indication: dto.indication,
    documents: dto.documents.map(parseLabDocument),
  };
}

export function parseLabReportRow(dto: LabReportRowDTO): LabReportRow {
  return { ...parseLabDocument(dto), ...pickReportJoins(dto) };
}

function pickReportJoins(dto: LabReportRowDTO) {
  return {
    patientId: dto.patientId,
    orderId: dto.orderId,
    patientName: dto.patientName,
    patientMrn: dto.patientMrn,
    accessionNumber: dto.accessionNumber,
    testName: dto.testName,
    testCode: dto.testCode,
  };
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/**
 * A report's size, for display.
 *
 * Not translated: KB and MB read the same in all three of the app's languages,
 * and the digits are already `tabular-nums` wherever this is rendered.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

// ---------- Upload guards (client-side, before any network call) ----------

/** Matches `MAX_LAB_DOCUMENT_BYTES` on the server. */
export const MAX_REPORT_BYTES = 20 * 1024 * 1024;

export type ReportRejection = 'not-pdf' | 'too-large' | 'empty';

/**
 * Check a chosen file before requesting an upload URL.
 *
 * The magic-byte read is the point: a file renamed to `.pdf` carries
 * `application/pdf` in `file.type` on most browsers, so the MIME string alone
 * proves nothing. The server re-checks the STORED type anyway — this exists so a
 * 20 MiB upload over a clinic's 3G tail fails in the file picker rather than
 * three minutes later on the confirm step.
 */
export async function validateReportFile(file: File): Promise<ReportRejection | null> {
  if (file.size === 0) return 'empty';
  if (file.size > MAX_REPORT_BYTES) return 'too-large';

  const header = new Uint8Array(await file.slice(0, 5).arrayBuffer());
  const magic = String.fromCharCode(...header);
  return magic === '%PDF-' ? null : 'not-pdf';
}

/**
 * SHA-256 of a file, or null when the browser will not do it.
 *
 * `crypto.subtle` is undefined outside a secure context, and a clinic running
 * the app on plain HTTP over a LAN is a real deployment. The hash only powers a
 * duplicate-upload warning, so its absence must not block the upload — hence
 * null rather than a throw.
 */
export async function hashReportFile(file: File): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  try {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    // The documented case — no `crypto.subtle` outside a secure context — is
    // already handled by the guard above, so reaching here means something else
    // failed: out of memory on a large file, or a digest the browser refused.
    // Still non-fatal (the hash only powers a warning), but it must not vanish
    // silently or the warning will look broken with nothing to explain it.
    console.warn('Could not hash report file for duplicate detection', err);
    return null;
  }
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

/** Thrown when the operator cancelled — the one rejection that is not a failure. */
export function abortedError(): ApiError {
  return new ApiError('UPLOAD_ABORTED', 'Upload cancelled', 0);
}

/**
 * PUT the bytes to S3 with progress and a working abort.
 *
 * XHR rather than `fetch` because `fetch` still has no upload progress, and a
 * report upload on a slow link with no visible progress reads as a hang.
 *
 * The already-aborted check on the first line is load-bearing.
 * `addEventListener('abort', …)` on a signal that has ALREADY fired never runs,
 * and by the time this is called the caller has been hashing (a full read of up
 * to 20 MiB) and waiting on a presign round-trip — all with the Cancel button on
 * screen. Without the check, cancelling during that window sends the file
 * anyway, the confirm follows, and the report attaches to the test while the
 * operator believes they stopped it. On the wrong patient, that is the worst
 * outcome this feature can produce.
 */
export function putWithProgress(
  url: string,
  file: File,
  opts: { onProgress?: (p: UploadProgress) => void; signal?: AbortSignal } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (opts.signal?.aborted) {
      reject(abortedError());
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'application/pdf');

    const onAbort = () => xhr.abort();
    // Removed on every exit path. An XHR that has settled must not keep the
    // signal alive, and a caller reusing one controller across retries would
    // otherwise accumulate listeners on it.
    const settle = (fn: () => void) => {
      opts.signal?.removeEventListener('abort', onAbort);
      fn();
    };

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.({ loaded: e.loaded, total: e.total });
    };
    xhr.onload = () =>
      settle(() => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else {
          reject(
            new ApiError('UPLOAD_FAILED', `Report upload failed (${xhr.status})`, xhr.status),
          );
        }
      });
    xhr.onerror = () =>
      settle(() => reject(new ApiError('UPLOAD_FAILED', 'Report upload failed', 0)));
    xhr.onabort = () => settle(() => reject(abortedError()));

    opts.signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(file);
  });
}

// ---------- API ----------

export const labApi = {
  /**
   * `includeInactive` is for the catalogue-management screen only. Ordering
   * omits it so a switched-off test can never be picked.
   */
  async catalogue(opts: { includeInactive?: boolean } = {}): Promise<LabCatalogueDTO> {
    return clinicApi.get<LabCatalogueDTO>(`lab/tests${opts.includeInactive ? '?includeInactive=true' : ''}`,
    );
  },

  /**
   * The clinic's laboratories.
   *
   * `includeInactive` splits the two callers on purpose: the new-order picker
   * omits it, because a retired lab must never be offered, while the worklist
   * filter passes it, because "show me what we sent to the lab we stopped using"
   * is a legitimate historical query.
   */
  async laboratories(opts: { includeInactive?: boolean } = {}): Promise<LabLaboratory[]> {
    const res = await clinicApi.get<{ laboratories: LabLaboratory[] }>(
      `lab/laboratories${opts.includeInactive ? '?includeInactive=true' : ''}`,
    );
    return res.laboratories;
  },

  async createLaboratory(input: CreateLabLaboratoryInput): Promise<LabLaboratory> {
    return clinicApi.post<LabLaboratory>('lab/laboratories', input);
  },

  async updateLaboratory(
    id: string,
    input: UpdateLabLaboratoryInput,
  ): Promise<LabLaboratory> {
    return clinicApi.patch<LabLaboratory>(`lab/laboratories/${id}`, input);
  },

  async worklist(query: LabWorklistQuery = {}): Promise<LabWorklistRow[]> {
    const res = await clinicApi.get<{ orders: (LabOrderSummaryDTO & {
      patientName: string;
      patientMrn: string;
    })[] }>(`lab/orders${qs({ ...query })}`);
    return res.orders.map((dto) => ({
      ...parseLabOrderSummary(dto),
      patientName: dto.patientName,
      patientMrn: dto.patientMrn,
    }));
  },

  async get(orderId: string): Promise<LabOrderDetail> {
    return parseLabOrderDetail(await clinicApi.get<LabOrderDetailDTO>(`lab/orders/${orderId}`));
  },

  async create(input: CreateLabOrderInput): Promise<LabOrderDetail> {
    return parseLabOrderDetail(await clinicApi.post<LabOrderDetailDTO>('lab/orders', input));
  },

  async update(orderId: string, input: UpdateLabOrderInput): Promise<LabOrderDetail> {
    return parseLabOrderDetail(
      await clinicApi.patch<LabOrderDetailDTO>(`lab/orders/${orderId}`, input),
    );
  },

  async cancel(orderId: string, reason: string): Promise<LabOrderDetail> {
    return parseLabOrderDetail(
      await clinicApi.post<LabOrderDetailDTO>(`lab/orders/${orderId}/cancel`, { reason }),
    );
  },

  async cancelTest(orderId: string, testId: string, reason: string): Promise<LabOrderDetail> {
    return parseLabOrderDetail(
      await clinicApi.post<LabOrderDetailDTO>(`lab/orders/${orderId}/tests/${testId}/cancel`,
        { reason },
      ),
    );
  },

  async collectSpecimen(
    orderId: string,
    input: { specimenType: SpecimenType; container?: string; collectedAt: string; labelNote?: string },
  ): Promise<LabOrderDetail> {
    return parseLabOrderDetail(
      await clinicApi.post<LabOrderDetailDTO>(`lab/orders/${orderId}/specimens`, input),
    );
  },

  async rejectSpecimen(
    specimenId: string,
    input: { reason: LabRejectionReason; note?: string },
  ): Promise<LabOrderDetail> {
    return parseLabOrderDetail(
      await clinicApi.post<LabOrderDetailDTO>(`lab/specimens/${specimenId}/reject`, input),
    );
  },

  async requestReportUploadUrl(
    orderId: string,
    testId: string,
  ): Promise<{ uploadUrl: string; key: string; expiresIn: number }> {
    return clinicApi.post(`lab/orders/${orderId}/tests/${testId}/documents/upload-url`, {
      contentType: 'application/pdf',
    });
  },

  async confirmReport(
    orderId: string,
    testId: string,
    input: ConfirmLabDocumentInput,
  ): Promise<{ order: LabOrderDetail; duplicateOf: string | null }> {
    const res = await clinicApi.put<{ order: LabOrderDetailDTO; duplicateOf: string | null }>(`lab/orders/${orderId}/tests/${testId}/documents`,
      input,
    );
    return { order: parseLabOrderDetail(res.order), duplicateOf: res.duplicateOf };
  },

  /**
   * Presign → PUT → confirm, the whole upload in one call.
   *
   * Deliberately NOT optimistic anywhere: a report that appears attached and
   * then turns out not to be is worse than a spinner.
   */
  async uploadReport(
    orderId: string,
    testId: string,
    file: File,
    opts: {
      supersedesDocumentId: string | null;
      reportDate?: string;
      labName?: string;
      onProgress?: (p: UploadProgress) => void;
      signal?: AbortSignal;
    },
  ): Promise<{ order: LabOrderDetail; duplicateOf: string | null }> {
    const rejection = await validateReportFile(file);
    if (rejection) {
      throw new ApiError('INVALID_FILE', `Report rejected: ${rejection}`, 0, { rejection });
    }

    // Cancellation is checked between every step, not just inside the PUT.
    // Hashing reads the whole file and presigning is a round-trip, and the
    // Cancel button is on screen throughout both — so each is a point where the
    // operator can have changed their mind, and each must be able to stop before
    // doing the irreversible thing (putting bytes, then attaching them).
    const stopIfCancelled = () => {
      if (opts.signal?.aborted) throw abortedError();
    };

    const sha256 = await hashReportFile(file);
    stopIfCancelled();

    const { uploadUrl, key } = await this.requestReportUploadUrl(orderId, testId);
    stopIfCancelled();

    await putWithProgress(uploadUrl, file, opts);
    stopIfCancelled();

    return this.confirmReport(orderId, testId, {
      key,
      fileName: file.name,
      byteSize: file.size,
      supersedesDocumentId: opts.supersedesDocumentId,
      ...(sha256 ? { sha256 } : {}),
      ...(opts.reportDate ? { reportDate: opts.reportDate } : {}),
      ...(opts.labName ? { labName: opts.labName } : {}),
    });
  },

  /**
   * Mint a short-lived URL for a report.
   *
   * Called on demand when someone opens a report — never prefetched into a list.
   * That is what makes the server's phi_access_log row mean "a person looked at
   * this", and what stops a 120s URL expiring inside a rendered page.
   */
  async reportUrl(documentId: string): Promise<{ url: string; expiresIn: number; fileName: string }> {
    return clinicApi.get(`lab/documents/${documentId}/url`);
  },

  async deleteReport(documentId: string): Promise<LabOrderDetail> {
    // `api.delete` is typed `T | undefined` because a handler may answer 204.
    // This one always returns the refreshed order — the caller needs it, since
    // removing a report changes the test's status and therefore the order's.
    // If the body is ever missing that is a server change, not a 204 to absorb.
    const dto = await clinicApi.delete<LabOrderDetailDTO>(`lab/documents/${documentId}`);
    if (!dto) {
      throw new ApiError('PARSE_ERROR', 'Delete returned no order to refresh from', 0);
    }
    return parseLabOrderDetail(dto);
  },

  async reports(query: LabReportsQuery = {}): Promise<LabReportRow[]> {
    const res = await clinicApi.get<{ documents: LabReportRowDTO[] }>(`lab/documents${qs({ ...query })}`,
    );
    return res.documents.map(parseLabReportRow);
  },

  async ordersForPatient(patientId: string, limit?: number): Promise<LabOrderSummary[]> {
    const dtos = await clinicApi.get<LabOrderSummaryDTO[]>(`patients/${patientId}/lab-orders${qs({ limit })}`,
    );
    return dtos.map(parseLabOrderSummary);
  },

  async createCatalogueEntry(input: {
    code: string;
    name: string;
    specimenType: SpecimenType;
    container?: string;
    sortOrder?: number;
    active?: boolean;
  }) {
    return clinicApi.post('lab/tests', input);
  },

  async updateCatalogueEntry(
    id: string,
    input: {
      name?: string;
      specimenType?: SpecimenType;
      container?: string | null;
      sortOrder?: number;
      active?: boolean;
    },
  ) {
    return clinicApi.patch(`lab/tests/${id}`, input);
  },
};
